import React, { useState, useEffect } from "react";
import LastSynced from "../../../layout/LastSynced";
import {
  Box,
  Grid,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  CircularProgress,
} from "@mui/material";
import StyledButton from "../../../ui/styledButton";
import { toast } from "react-toastify";
import {
  getAppVersionConfig,
  updateAppVersionConfig,
} from "../../../services/appVersionAPI";

const defaultSettings = {
  customer: {
    android: {
      version: "0.0.5",
      build_number: 5,
      force_update: false,
      store_url: "https://play.google.com/store/apps/details?id=com.goecm.customer",
      message: "A new version of GOECM is available. Please update to enjoy the latest features and improvements.",
    },
    ios: {
      version: "0.0.5",
      build_number: 5,
      force_update: false,
      store_url: "https://apps.apple.com/app/goecm/id0000000000",
      message: "A new version of GOECM is available. Please update to enjoy the latest features and improvements.",
    },
  },
};

const darkInputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "secondary.cardbg",
    color: "#FFFFFF",
    borderRadius: "6px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.35)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2B80FF",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": {
    color: "#FFFFFF",
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8E92A4",
    opacity: 1,
  },
};

export default function AppVersionManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(defaultSettings);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await getAppVersionConfig();
      if (data) {
        if (data.customer) {
          setFormData(data);
        } else if (data.android || data.ios) {
          setFormData({ customer: data });
        } else if (data.application?.citizen_connect || data.application?.goecm) {
          const appData = data.application.goecm || data.application.citizen_connect;
          setFormData({ customer: appData });
        }
      }
    } catch (error) {
      toast.error("Failed to load app version configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (platform, field, value) => {
    setFormData((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        [platform]: {
          ...prev.customer?.[platform],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateAppVersionConfig(formData);
      toast.success("App version settings updated successfully!");
    } catch (error) {
      toast.error("Failed to update app version settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <LastSynced heading="App Version Settings" />
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "350px" }}>
          <CircularProgress sx={{ color: "#2B80FF" }} />
        </Box>
      </Box>
    );
  }

  const android = formData.customer?.android || defaultSettings.customer.android;
  const ios = formData.customer?.ios || defaultSettings.customer.ios;

  return (
    <Box>
      <LastSynced heading="App Version Settings" />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: "secondary.contrastText", fontSize: "14px" }}>
            Configure mobile application version numbers, mandatory/flexible update policies, and store links.
          </Typography>
          <StyledButton
            variant="secondary"
            width="180"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save Changes"}
          </StyledButton>
        </Box>

        <Grid container spacing={3}>
          {/* Android App Configuration Card */}
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                backgroundColor: "secondary.main",
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                p: { xs: 2.5, md: 3.5 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "6px",
                      backgroundColor: "rgba(63, 199, 154, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#3FC79A",
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    🤖
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "17px" }}>
                      Android App
                    </Typography>
                    <Typography variant="caption" sx={{ color: "secondary.contrastText" }}>
                      Google Play Store Configuration
                    </Typography>
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(android.force_update)}
                      onChange={(e) => handleInputChange("android", "force_update", e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#FF5252",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#FF5252",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: android.force_update ? "#FF7575" : "secondary.contrastText",
                      }}
                    >
                      {android.force_update ? "Force Update" : "Flexible"}
                    </Typography>
                  }
                />
              </Box>

              <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.10)", mb: 3 }} />

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Latest Version String *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 1.0.0"
                    value={android.version || ""}
                    onChange={(e) => handleInputChange("android", "version", e.target.value)}
                    sx={darkInputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Build Number / Code
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="e.g. 10"
                    value={android.build_number || ""}
                    onChange={(e) => handleInputChange("android", "build_number", parseInt(e.target.value) || 0)}
                    sx={darkInputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Google Play Store URL *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://play.google.com/store/apps/details?id=..."
                    value={android.store_url || ""}
                    onChange={(e) => handleInputChange("android", "store_url", e.target.value)}
                    sx={darkInputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Update Announcement Message
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder="Enter update notes or release announcements..."
                    value={android.message || ""}
                    onChange={(e) => handleInputChange("android", "message", e.target.value)}
                    sx={darkInputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* iOS App Configuration Card */}
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                backgroundColor: "secondary.main",
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                p: { xs: 2.5, md: 3.5 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "6px",
                      backgroundColor: "rgba(43, 128, 255, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#2B80FF",
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    🍏
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "17px" }}>
                      iOS App
                    </Typography>
                    <Typography variant="caption" sx={{ color: "secondary.contrastText" }}>
                      Apple App Store Configuration
                    </Typography>
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(ios.force_update)}
                      onChange={(e) => handleInputChange("ios", "force_update", e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#FF5252",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#FF5252",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: ios.force_update ? "#FF7575" : "secondary.contrastText",
                      }}
                    >
                      {ios.force_update ? "Force Update" : "Flexible"}
                    </Typography>
                  }
                />
              </Box>

              <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.10)", mb: 3 }} />

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Latest Version String *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 1.0.0"
                    value={ios.version || ""}
                    onChange={(e) => handleInputChange("ios", "version", e.target.value)}
                    sx={darkInputSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Build Number / Code
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="e.g. 10"
                    value={ios.build_number || ""}
                    onChange={(e) => handleInputChange("ios", "build_number", parseInt(e.target.value) || 0)}
                    sx={darkInputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Apple App Store URL *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://apps.apple.com/app/..."
                    value={ios.store_url || ""}
                    onChange={(e) => handleInputChange("ios", "store_url", e.target.value)}
                    sx={darkInputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: "secondary.contrastText", fontWeight: 600, display: "block", mb: 0.8 }}
                  >
                    Update Announcement Message
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder="Enter update notes or release announcements..."
                    value={ios.message || ""}
                    onChange={(e) => handleInputChange("ios", "message", e.target.value)}
                    sx={darkInputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
